import { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './AdminDashboard.module.css';
import UserModal from './UserModal';
import DepositModal from './DepositModal';
import OrderModal from './OrderModal';
import BotModal from './BotModal';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../AdminAuthContext';


const API_BASE = '/api/admin';

const AdminDashboard = () => {
  const { setIsAdminLoggedIn } = useAdminAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [pnl, setPnl] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bots, setBots] = useState([]);

  //Pagination
  const [userPage, setUserPage] = useState(1);
  const USERS_PER_PAGE = 10;

  const [depositPage, setDepositPage] = useState(1);
  const DEPOSITS_PER_PAGE = 10;

  const [orderPage, setOrderPage] = useState(1);
  const ORDERS_PER_PAGE = 10;

  const [botPage, setBotPage] = useState(1);
  const BOTS_PER_PAGE = 10;

  //User Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userCount, setUserCount] = useState(0);

  //Deposit Modal
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);

  //Order Modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  //Bot Modal
  const [selectedBot, setSelectedBot] = useState(null);
  const [showBotModal, setShowBotModal] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("adminAccessToken");
  
      const userRes = await axios.get(`${API_BASE}/users/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(userRes.data);
  
      const userCountRes = await axios.get(`${API_BASE}/users/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUserCount(userCountRes.data.count);
  
      const depositRes = await axios.get(`${API_BASE}/deposit?period=week`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDeposits(depositRes.data.data || []);

      const orderRes = await axios.get(`${API_BASE}/orders?period=week`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setOrders(orderRes.data.data || []);
  
      const pnlRes = await axios.get(`${API_BASE}/pnl?period=week`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPnl(pnlRes.data.data || []);
  
      const botRes = await axios.get(`${API_BASE}/bots`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBots(botRes.data || []);
    } catch (err) {
      console.error('Admin dashboard error:', err);
      if (err.response && err.response.status === 403) {
        localStorage.removeItem("adminAccessToken");
        delete axios.defaults.headers.common['Authorization'];
        navigate('/admin/login'); 
      }
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // User Functions ===================================================
  const handleUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("adminAccessToken");
      const res = await axios.get(`${API_BASE}/users/${userId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSelectedUser(res.data);
      setShowUserModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleSuspendToggle = async (user) => {
    try {
      const token = localStorage.getItem("adminAccessToken");
      const res = await axios.put(
        `${API_BASE}/users/${user.id}/suspend`,
        { suspend: user.is_active },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const updatedUsers = users.map(u =>
        u.id === user.id ? { ...u, is_active: !user.is_active } : u
      );
      setUsers(updatedUsers);
      setSelectedUser(prev => ({ ...prev, is_active: !user.is_active }));
    } catch (error) {
      console.error('Error suspending user:', error);
    }
  };
  //===================================================

  //Deposit Functions ===================================================
  const handleDepositDetails = async (depositId) => {
    try {
      const token = localStorage.getItem("adminAccessToken");
      const res = await axios.get(`${API_BASE}/deposit/${depositId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSelectedDeposit(res.data);
      setShowDepositModal(true);
    } catch (error) {
      console.error('Error fetching deposit details:', error);
    }
  };
  
  const handleRejectDeposit = async (depositId) => {
    try {
      const token = localStorage.getItem("adminAccessToken");
      await axios.post(`${API_BASE}/deposit/${depositId}/reject`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const updatedDeposits = deposits.map(d =>
        d.id === depositId ? { ...d, status: 'fail' } : d
      );
      setDeposits(updatedDeposits);
    } catch (error) {
      console.error('Error rejecting deposit:', error);
    }
  };
  //===================================================
  
  //Order Functions ===================================================
  const handleOrderDetails = async (orderId) => {
    try {
      const token = localStorage.getItem("adminAccessToken");
      const res = await axios.get(`${API_BASE}/orders/${orderId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSelectedOrder(res.data);
      setShowOrderModal(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };
  //===================================================

  //Bot Functions ===================================================
  const handleBotDetails = async (botId) => {
    try {
      const token = localStorage.getItem("adminAccessToken");
      const res = await axios.get(`${API_BASE}/bots/${botId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSelectedBot(res.data);
      setShowBotModal(true);
    } catch (err) {
      console.error('Error fetching bot details:', err);
    }
  };
  //===================================================

  return (
    <div className={styles.dashboard}>
      <h1>Admin Dashboard</h1>

      <div className={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>All Users</h3>
          <span style={{ fontSize: '16px', fontWeight: '500', color: '#4b5563' }}>
            User Count: {userCount}
          </span>
      </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Email</th>
              <th>Username</th>
              <th>Is Active</th>
              <th>Is Staff</th>
              <th>Bot Active</th>
              <th>Sub UID</th>
            </tr>
          </thead>
          <tbody>
          {users
            .slice((userPage - 1) * USERS_PER_PAGE, userPage * USERS_PER_PAGE)
            .map(user => (
              <tr key={user.id} onClick={() => handleUserDetails(user.id)}>
                <td>{user.id}</td>
                <td>{user.email}</td>
                <td>{user.username}</td>
                <td>{user.is_active ? 'Yes' : 'No'}</td>
                <td>{user.is_staff ? 'Yes' : 'No'}</td>
                <td>{user.is_bot_active ? 'Yes' : 'No'}</td>
                <td>{user.sub_uid || '-'}</td>
              </tr>
          ))}
          </tbody>
        </table>
        <div className={`${styles.pagination}`}>
        <button
          onClick={() => setUserPage(p => Math.max(p - 1, 1))}
          disabled={userPage === 1}
        >
          Previous
        </button>
        <span>Page {userPage} / {Math.ceil(users.length / USERS_PER_PAGE)}</span>
        <button
          onClick={() => setUserPage(p => Math.min(p + 1, Math.ceil(users.length / USERS_PER_PAGE)))}
          disabled={userPage === Math.ceil(users.length / USERS_PER_PAGE)}
        >
          Next
        </button>
      </div>
      </div>
      {showUserModal && (
        <UserModal
          user={selectedUser}
          onClose={() => setShowUserModal(false)}
          onSuspendToggle={handleSuspendToggle}
        />
      )}

      <div className={styles.card}>
        <h3>Recent Deposits</h3>
        <table className={styles.table}>
          <thead>
          <tr>
              <th>ID</th>
              <th>Sub UID</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Coin</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
          {deposits
            .slice((depositPage - 1) * DEPOSITS_PER_PAGE, depositPage * DEPOSITS_PER_PAGE)
            .map(d => (
              <tr key={d.id} onClick={() => handleDepositDetails(d.id)}>
                <td>{d.id}</td>
                <td>{d.sub_uid}</td>
                <td>{d.amount}</td>
                <td>{d.status}</td>
                <td>{d.coin_symbol}</td>
                <td>{new Date(d.created_at).toLocaleString()}</td>
              </tr>
          ))}
        </tbody>
        </table>
        <div className={styles.pagination}>
          <button
            onClick={() => setDepositPage(p => Math.max(p - 1, 1))}
            disabled={depositPage === 1}
          >
            Previous
          </button>
          <span>Page {depositPage} / {Math.ceil(deposits.length / DEPOSITS_PER_PAGE)}</span>
          <button
            onClick={() => setDepositPage(p => Math.min(p + 1, Math.ceil(deposits.length / DEPOSITS_PER_PAGE)))}
            disabled={depositPage === Math.ceil(deposits.length / DEPOSITS_PER_PAGE)}
          >
            Next
          </button>
        </div>
      </div>
      {showDepositModal && (
        <DepositModal
          deposit={selectedDeposit}
          onClose={() => setShowDepositModal(false)}
          onReject={handleRejectDeposit}
        />
      )}
      <div className={styles.card}>
        <h3>Weekly PnL</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>PnL</th>
            </tr>
          </thead>
          <tbody>
            {pnl.map((item, i) => (
              <tr key={i}>
                <td>{item.date || item.week_start || item.month}</td>
                <td>{item.pnl}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.card}>
        <h3>Trade Orders</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Order ID</th>
              <th>Side</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {orders
              .slice((orderPage - 1) * ORDERS_PER_PAGE, orderPage * ORDERS_PER_PAGE)
              .map(o => (
                <tr key={o.id} onClick={() => handleOrderDetails(o.id)}>
                  <td>{o.id}</td>
                  <td>{o.order_id}</td>
                  <td>{o.side}</td>
                  <td>{o.qty}</td>
                  <td>{o.price}</td>
                  <td>{new Date(o.created_at).toLocaleString()}</td>
                </tr>
            ))}
          </tbody>
        </table>
        <div className={styles.pagination}>
          <button
            onClick={() => setOrderPage(p => Math.max(p - 1, 1))}
            disabled={orderPage === 1}
          >
            Previous
          </button>
          <span>Page {orderPage} / {Math.ceil(orders.length / ORDERS_PER_PAGE)}</span>
          <button
            onClick={() => setOrderPage(p => Math.min(p + 1, Math.ceil(orders.length / ORDERS_PER_PAGE)))}
            disabled={orderPage === Math.ceil(orders.length / ORDERS_PER_PAGE)}
          >
            Next
          </button>
        </div>
      </div>
      {showOrderModal && (
        <OrderModal
          order={selectedOrder}
          onClose={() => setShowOrderModal(false)}
        />
      )}
      <div className={styles.card}>
        <h3>Active Bots</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>User ID</th>
              <th>Coin</th>
              <th>Bot Status</th>
              <th>Buy Count</th>
              <th>Error Status</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {bots
              .slice((botPage - 1) * BOTS_PER_PAGE, botPage * BOTS_PER_PAGE)
              .map(bot => (
                <tr key={bot.id} onClick={() => handleBotDetails(bot.id)}>
                  <td>{bot.id}</td>
                  <td>{bot.user_id}</td>
                  <td>{bot.coin_symbol}</td>
                  <td>{bot.bot_status ? 'Running' : 'Stopped'}</td>
                  <td>{bot.buy_count}</td>
                  <td>{bot.error_status}</td>
                  <td>{new Date(bot.created_at).toLocaleString()}</td>
                </tr>
            ))}
          </tbody>
        </table>
        <div className={styles.pagination}>
          <button
            onClick={() => setBotPage(p => Math.max(p - 1, 1))}
            disabled={botPage === 1}
          >
            Previous
          </button>
          <span>Page {botPage} / {Math.ceil(bots.length / BOTS_PER_PAGE)}</span>
          <button
            onClick={() => setBotPage(p => Math.min(p + 1, Math.ceil(bots.length / BOTS_PER_PAGE)))}
            disabled={botPage === Math.ceil(bots.length / BOTS_PER_PAGE)}
          >
            Next
          </button>
        </div>
      </div>
      {showBotModal && (
        <BotModal
          bot={selectedBot}
          onClose={() => setShowBotModal(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;